<script setup lang="ts">
  import { computed, inject, type Ref, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    ClipboardPen,
    ChevronsLeft,
    ChevronDown,
    ChevronUp,
    CircleCheck,
    Circle,
    XCircle,
    Repeat
  } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { type ClientOrganisation } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import clientPortalOrganisationCard from '@/components/clientPortal/clientPortalOrganisationCardV3.vue'
  import { useQuestionnaireStore } from '@/store/questionnaireStore.ts'
  import type { Page } from '@/types'
  import { useQuestionnaire } from '@/composables/useQuestionnaire'
  import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import { getEntityAccusativeDE } from '@/i18n/entityAccusative'

  const route = useRoute()
  const router = useRouter()
  const { t, locale } = useTranslation({
    en: {
      requiredActions: 'Required Actions',
      questionnaire: 'Questionnaire',
      pdfDocument: 'PDF Document',
      loadingQuestionnaireData: 'Loading questionnaire data...',
      poweredBy: 'Powered by',
      overview: 'Overview',
      repeatableSectionTooltip: 'This section can be repeated multiple times',
      repeatSectionPrompt: 'Add another {participantType}?',
      repeatSectionPromptFallback: 'Add another section?',
      skipToMain: 'Skip to main content',
      closeSidebar: 'Close sidebar navigation',
      sectionExpanded: 'Section {title}, expanded',
      sectionCollapsed: 'Section {title}, collapsed',
      questionAnswered: 'Answered',
      questionUnanswered: 'Not yet answered',
      questionInvalid: 'Invalid answer',
      currentQuestion: 'Current question',
      questionnaireNavigation: 'Questionnaire navigation'
    },
    de: {
      requiredActions: 'Erforderliche Aktionen',
      questionnaire: 'Fragebogen',
      pdfDocument: 'PDF-Dokument',
      loadingQuestionnaireData: 'Lade Fragebogen-Daten...',
      poweredBy: 'Powered by',
      overview: 'Übersicht',
      repeatableSectionTooltip: 'Dieser Abschnitt kann mehrfach wiederholt werden',
      repeatSectionPrompt: '{accusativePhrase} hinzufügen?',
      repeatSectionPromptFallback: 'Abschnitt hinzufügen?',
      skipToMain: 'Zum Hauptinhalt springen',
      closeSidebar: 'Seitennavigation schließen',
      sectionExpanded: 'Abschnitt {title}, ausgeklappt',
      sectionCollapsed: 'Abschnitt {title}, eingeklappt',
      questionAnswered: 'Beantwortet',
      questionUnanswered: 'Noch nicht beantwortet',
      questionInvalid: 'Ungültige Antwort',
      currentQuestion: 'Aktuelle Frage',
      questionnaireNavigation: 'Fragebogen-Navigation'
    }
  })

  const org = inject<Ref<ClientOrganisation | null>>('org')
  const questionnaire = inject<Ref<any | null>>('questionnaire', ref(null))
  const questionnaireStore = useQuestionnaireStore()

  const { fieldValidationErrors, saveFieldData } = useQuestionnaire()

  const isQuestionnaireRoute = computed(() => {
    return route.path.includes('/questionnaires/')
  })

  const isExternalQuestionnaireRoute = computed(() => {
    return route.path.includes('/external/')
  })

  const navigationItems = [
    {
      id: 'home',
      label: t('requiredActions'),
      icon: ClipboardPen,
      route: ROUTES.CLIENT_PORTAL.HOME,
      isActive: computed(() => route.path === ROUTES.CLIENT_PORTAL.HOME)
    }
  ]

  // Questionnaire specific logic
  const openSections = ref<string[]>([])
  const rawSchema = computed(() => {
    if (questionnaire.value?.schema) {
      return questionnaire.value.schema as Page[]
    }
    return []
  })

  const activeField = computed(() => questionnaireStore.activeFieldId)

  const toggleSection = (sectionUuid: string) => {
    if (openSections.value.includes(sectionUuid)) {
      openSections.value = openSections.value.filter((uuid) => uuid !== sectionUuid)
    } else {
      openSections.value = [sectionUuid]
    }
  }

  const processChange = async (uuid: string, value: any) => {
    if (!questionnaire.value) return

    await saveFieldData(uuid, value, questionnaire.value)
  }

  const handleItemClick = async (uuid: string) => {
    if (isQuestionnaireRoute.value) {
      // Save current field data before jumping to new field
      const currentFieldId = questionnaireStore.activeFieldId
      const currentFieldValue = questionnaireStore.formData[currentFieldId]

      // Only save if the current field has a value
      if (currentFieldId && currentFieldValue !== undefined && currentFieldValue !== '') {
        // Trigger validation for the current field before saving
        // This ensures we have the latest validation state
        await processChange(currentFieldId, currentFieldValue)
      }

      questionnaireStore.jumpToField(uuid)

      if (window.innerWidth < 768) {
        emit('toggle')
      }
    }
  }

  const isQuestionAnswered = (uuid: string) => {
    const value = questionnaireStore.formData[uuid]

    // Find the field in the schema to check if empty string is a valid choice
    const field = rawSchema.value
      .flatMap((section) => section.items)
      .find((item) => item.uuid === uuid)

    const hasEmptyAsChoice = field && field.choices && field.choices.includes('')

    const hasValue = (() => {
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0
      }
      return (
        value !== undefined &&
        value !== null &&
        (hasEmptyAsChoice || value !== '') &&
        (Array.isArray(value) ? value.length > 0 : true)
      )
    })()

    if (!hasValue) {
      return false
    }

    const hasValidationError = fieldValidationErrors.value[uuid]

    return hasValue && !hasValidationError
  }

  const isQuestionInvalid = (uuid: string) => {
    const value = questionnaireStore.formData[uuid]

    // Find the field in the schema to check if empty string is a valid choice
    const field = rawSchema.value
      .flatMap((section) => section.items)
      .find((item) => item.uuid === uuid)

    const hasEmptyAsChoice = field && field.choices && field.choices.includes('')

    const hasValue = (() => {
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0
      }
      return (
        value !== undefined &&
        value !== null &&
        (hasEmptyAsChoice || value !== '') &&
        (Array.isArray(value) ? value.length > 0 : true)
      )
    })()

    const hasValidationError = fieldValidationErrors.value[uuid]
    return hasValue && hasValidationError
  }

  const isActiveField = (uuid: string) => {
    // If the repeat section prompt is showing, no regular question should be active
    if (questionnaireStore.showRepeatSectionPrompt) {
      return false
    }
    return uuid === activeField.value
  }

  const updateOpenSections = () => {
    if (!questionnaire.value) {
      return
    }

    if (activeField.value) {
      const currentSection = rawSchema.value.find((section) =>
        section.items.some((item) => item.uuid === activeField.value)
      )
      if (currentSection) {
        // Use uuid if available, otherwise fall back to page
        const sectionId = (currentSection as any).uuid || currentSection.page?.toString() || ''
        openSections.value = [sectionId]
      } else {
        const firstSection = rawSchema.value[0]
        const sectionId = (firstSection as any)?.uuid || firstSection?.page?.toString() || ''
        openSections.value = rawSchema.value.length > 0 ? [sectionId] : []
      }
    } else {
      // If no active field, open the first section
      const firstSection = rawSchema.value[0]
      const sectionId = (firstSection as any)?.uuid || firstSection?.page?.toString() || ''
      openSections.value = rawSchema.value.length > 0 ? [sectionId] : []
    }
  }

  watch(
    () => questionnaire.value,
    (newQuestionnaire) => {
      if (isQuestionnaireRoute.value && newQuestionnaire && newQuestionnaire.schema) {
        updateOpenSections()
      }
    },
    { immediate: true }
  )

  watch(
    () => activeField.value,
    () => {
      if (isQuestionnaireRoute.value && questionnaire.value) {
        updateOpenSections()
      }
    }
  )

  if (isQuestionnaireRoute.value && questionnaire.value && questionnaire.value.schema) {
    updateOpenSections()
  }

  const handleNavigation = (item: any) => {
    if (item.route) {
      const urlParams = new URLSearchParams(window.location.search)
      const sessionToken = urlParams.get('session_token')
      const accessToken = urlParams.get('access_token')

      // Prefer session_token over access_token, only include one
      let url = item.route
      if (sessionToken) {
        url = `${item.route}?session_token=${sessionToken}`
      } else if (accessToken) {
        url = `${item.route}?access_token=${accessToken}`
      }
      router.push(url)
    }
  }

  // Get translated participant type name for a section
  const getParticipantTypeName = (section: any) => {
    const participantType = (section as any).participant_type
    if (!participantType) {
      return 'section'
    }

    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return entityTranslations[participantType as keyof typeof entityTranslations] || participantType
  }

  // Get the repeat section prompt with correct German grammar
  const getRepeatSectionPrompt = (section: any): string => {
    const participantType = (section as any).participant_type
    if (!participantType) {
      return t('repeatSectionPromptFallback')
    }
    if (locale.value === 'de') {
      const accusativePhrase = getEntityAccusativeDE(participantType)
      if (!accusativePhrase) {
        return t('repeatSectionPromptFallback')
      }
      return t('repeatSectionPrompt', { accusativePhrase })
    }
    return t('repeatSectionPrompt', { participantType: getParticipantTypeName(section) })
  }

  // Get section info for the repeat prompt
  const getSectionInfoForRepeat = (section: any, sectionIndex: number) => {
    const sectionAny = section as any
    const participantType = sectionAny.participant_type || null

    // Calculate start and end field indices for this section
    let startFieldIndex = 0
    for (let i = 0; i < sectionIndex; i++) {
      startFieldIndex += rawSchema.value[i].items?.length || 0
    }
    const endFieldIndex = startFieldIndex + (section.items?.length || 0) - 1

    return {
      section,
      sectionIndex,
      startFieldIndex,
      endFieldIndex,
      isRepeatable: sectionAny.repeatable === true,
      participantType
    }
  }

  // Handle clicking on the repeat prompt "question"
  const handleRepeatPromptClick = async (section: any, sectionIndex: number) => {
    if (!isQuestionnaireRoute.value) return

    // Save current field data before showing the prompt
    const currentFieldId = questionnaireStore.activeFieldId
    const currentFieldValue = questionnaireStore.formData[currentFieldId]

    if (currentFieldId && currentFieldValue !== undefined && currentFieldValue !== '') {
      await processChange(currentFieldId, currentFieldValue)
    }

    // Set the repeat section prompt state
    const sectionInfo = getSectionInfoForRepeat(section, sectionIndex)
    questionnaireStore.setRepeatSectionPrompt(true, sectionInfo)

    if (window.innerWidth < 768) {
      emit('toggle')
    }
  }

  const getQuestionStatusLabel = (uuid: string): string => {
    if (isQuestionAnswered(uuid)) return t('questionAnswered')
    if (isQuestionInvalid(uuid)) return t('questionInvalid')
    return t('questionUnanswered')
  }

  const isSectionOpen = (section: any): boolean => {
    return openSections.value.includes((section as any).uuid || section.page?.toString() || '')
  }

  const skipToMainContent = () => {
    // External questionnaire: stay on page and focus main content (token is questionnaire-specific).
    // In-portal questionnaire: navigate back to portal home with session/access token.
    const shouldNavigateHome = isQuestionnaireRoute.value && !isExternalQuestionnaireRoute.value

    if (shouldNavigateHome) {
      const urlParams = new URLSearchParams(window.location.search)
      const sessionToken = urlParams.get('session_token')
      const accessToken = urlParams.get('access_token')

      let url = ROUTES.CLIENT_PORTAL.HOME
      if (sessionToken) {
        url = `${url}?session_token=${sessionToken}`
      } else if (accessToken) {
        url = `${url}?access_token=${accessToken}`
      }
      router.push(url)
    } else {
      const mainEl = document.getElementById('main-content')
      if (mainEl) {
        mainEl.focus()
      }
    }
  }

  const emit = defineEmits(['toggle'])
</script>

<template>
  <div
    data-cy="Sidebar"
    class="flex h-full w-[250px] flex-col overflow-y-hidden border-r border-gray-200 bg-white"
  >
    <div class="flex h-16 items-center justify-end px-6">
      <button
        data-cy="SidebarCloseButton"
        :aria-label="t('closeSidebar')"
        class="rounded-md p-1 transition-colors hover:bg-gray-100"
        @click="emit('toggle')"
      >
        <ChevronsLeft
          class="h-5 w-5 text-gray-600"
          aria-hidden="true"
        />
      </button>
    </div>

    <nav
      :aria-label="isQuestionnaireRoute ? t('questionnaireNavigation') : t('requiredActions')"
      class="flex flex-1 flex-col gap-4 overflow-y-hidden px-4 pt-1"
    >
      <template v-if="!isQuestionnaireRoute">
        <a
          href="#main-content"
          class="skip-to-main-content"
          @click.prevent="skipToMainContent"
        >
          {{ t('skipToMain') }}
        </a>
        <div class="flex flex-col gap-2">
          <button
            v-for="item in navigationItems"
            :key="item.id"
            type="button"
            :aria-current="item.isActive.value ? 'page' : undefined"
            class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-base font-normal
              transition-colors"
            :class="[
              item.isActive
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            ]"
            @click="handleNavigation(item)"
          >
            <component
              :is="item.icon"
              class="h-6 w-6 shrink-0"
              :class="item.isActive ? 'text-gray-900' : 'text-gray-500'"
              aria-hidden="true"
            />
            {{ item.label }}
          </button>
        </div>
      </template>

      <!-- Questionnaire loading state -->
      <div
        v-if="isQuestionnaireRoute && !questionnaire"
        class="mb-4"
        role="status"
      >
        <div class="mb-2 text-xs text-gray-500">
          {{ t('loadingQuestionnaireData') }}
        </div>
      </div>

      <!-- Questionnaire title -->
      <div
        v-if="isQuestionnaireRoute && questionnaire"
        class="mb-4 flex-shrink-0"
      >
        <h2 class="mb-3 text-lg font-semibold text-gray-900">
          {{ t('overview') }}
        </h2>
        <a
          href="#main-content"
          class="skip-to-main-content"
          @click.prevent="skipToMainContent"
        >
          {{ t('skipToMain') }}
        </a>
      </div>

      <template v-if="isQuestionnaireRoute && questionnaire">
        <ScrollArea class="min-h-0 flex-1">
          <div
            v-for="section in rawSchema"
            :key="(section as any).uuid || section.page?.toString()"
            class="mb-4"
          >
            <button
              type="button"
              :aria-expanded="isSectionOpen(section)"
              :aria-controls="`section-panel-${(section as any).uuid || section.page?.toString() || ''}`"
              class="flex w-full cursor-pointer items-start justify-between rounded-lg bg-white p-3 text-left
                duration-300 hover:bg-gray-50"
              @click="toggleSection((section as any).uuid || section.page?.toString() || '')"
            >
              <div class="flex min-w-0 flex-1 items-center gap-1.5">
                <h3 class="break-words text-sm font-medium text-gray-900">
                  {{ section.title }}
                </h3>
              </div>
              <ChevronDown
                v-if="!isSectionOpen(section)"
                class="h-5 w-5 text-gray-500"
                aria-hidden="true"
              />
              <ChevronUp
                v-else
                class="h-5 w-5 text-gray-500"
                aria-hidden="true"
              />
            </button>

            <transition
              enter-active-class="transition-all duration-300 ease-in-out"
              enter-from-class="opacity-0 transform -translate-y-2"
              enter-to-class="opacity-100 transform translate-y-0"
              leave-active-class="transition-all duration-300 ease-in-out"
              leave-from-class="opacity-100 transform translate-y-0"
              leave-to-class="opacity-0 transform -translate-y-2"
            >
              <ul
                v-if="isSectionOpen(section)"
                :id="`section-panel-${(section as any).uuid || section.page?.toString() || ''}`"
                role="list"
                class="ml-4 mt-2"
              >
                <li
                  v-for="item in section.items"
                  :key="item.uuid"
                  role="listitem"
                >
                  <button
                    type="button"
                    :class="[
                      `flex w-full cursor-pointer items-center rounded-r-md border-l-2 border-gray-200 px-3 py-2 text-left
                      text-sm font-normal transition-colors`,
                      isActiveField(item.uuid)
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    ]"
                    :aria-current="isActiveField(item.uuid) ? 'step' : undefined"
                    :data-cy="`SidebarQuestionItem-${item.uuid}`"
                    @click="handleItemClick(item.uuid)"
                  >
                    <CircleCheck
                      v-if="isQuestionAnswered(item.uuid)"
                      class="mr-3 h-5 w-5 flex-shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                    <XCircle
                      v-else-if="isQuestionInvalid(item.uuid)"
                      class="mr-3 h-5 w-5 flex-shrink-0 text-red-500"
                      aria-hidden="true"
                    />
                    <Circle
                      v-else
                      class="mr-3 h-5 w-5 flex-shrink-0 text-gray-300"
                      aria-hidden="true"
                    />
                    <span class="break-words">
                      {{ item.label }}
                    </span>
                    <span class="sr-only">
                      — {{ getQuestionStatusLabel(item.uuid) }}
                      <template v-if="isActiveField(item.uuid)">
                        , {{ t('currentQuestion') }}
                      </template>
                    </span>
                  </button>
                </li>

                <!-- Repeat prompt virtual question for repeatable sections -->
                <li
                  v-if="(section as any).repeatable"
                  role="listitem"
                >
                  <button
                    type="button"
                    :class="[
                      `flex w-full cursor-pointer items-center rounded-r-md border-l-2 border-gray-200 px-3 py-2 text-left text-sm font-normal transition-colors`,
                      questionnaireStore.showRepeatSectionPrompt &&
                      questionnaireStore.pendingRepeatSectionInfo?.section.uuid ===
                        (section as any).uuid
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    ]"
                    :data-cy="`SidebarRepeatPrompt-${(section as any).uuid}`"
                    @click="handleRepeatPromptClick(section, rawSchema.indexOf(section))"
                  >
                    <div
                      class="mr-3 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-blue-100"
                      aria-hidden="true"
                    >
                      <Repeat class="h-3 w-3 text-blue-600" />
                    </div>
                    <span class="break-words italic">
                      {{ getRepeatSectionPrompt(section) }}
                    </span>
                  </button>
                </li>
              </ul>
            </transition>
          </div>
        </ScrollArea>
      </template>
    </nav>

    <div
      v-if="org"
      class="p-3"
    >
      <clientPortalOrganisationCard />
    </div>

    <footer class="flex w-full items-center justify-center bg-white py-1">
      <div class="font-[Inter] text-sm font-medium tracking-tight text-gray-600">
        {{ t('poweredBy') }}
      </div>
      <img
        class="ml-[6px] h-[24px] w-[56px]"
        src="https://s3-eu-central-1.ionoscloud.com/jupus/images/logo.svg"
        alt="Jupus Logo"
      />
    </footer>
  </div>
</template>

<style scoped>
  /* Figma node 13609-153451: Skip to main content – ghost button, visible only on focus */
  .skip-to-main-content {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #1f2937;
    font-family: Inter, ui-sans-serif, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0;
    text-align: center;
    text-decoration: underline;
  }

  .skip-to-main-content:focus {
    position: static;
    width: 100%;
    height: auto;
    margin: 0.5rem 0;
    padding: 6px;
    overflow: visible;
    clip: auto;
    white-space: normal;
    border: none;
    outline: 3px solid #9333ea;
    outline-offset: 2px;
  }
</style>
